import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  FaBullhorn,
  FaTimes,
  FaInfoCircle,
  FaExclamationTriangle,
  FaCheckCircle,
  FaBell,
} from 'react-icons/fa';
import { apiHelper } from '../../utils/api';
import logger from '../../utils/logger';
import './AnnouncementModal.scss';

const ICON_MAP = {
  info: FaInfoCircle,
  announcement: FaBullhorn,
  warning: FaExclamationTriangle,
  celebration: FaCheckCircle,
  update: FaBell,
  maintenance: FaExclamationTriangle,
};

const THEME_COLORS = {
  default: '#6366f1',
  info: '#3b82f6',
  success: '#10b981',
  warning: '#f59e0b',
  urgent: '#ef4444',
};

function getAccentColor(ann) {
  return ann?.customStyle?.borderColor || THEME_COLORS[ann?.theme] || THEME_COLORS.default;
}

const AnnouncementModal = () => {
  const [modalAnnouncement, setModalAnnouncement] = useState(null);

  // Track shown announcements by a key combining id + updatedAt,
  // so an updated announcement re-shows the modal to the user.
  const shownModalKeysRef = useRef(new Set());
  const isFetchingRef = useRef(false);
  const backoffUntilRef = useRef(0);

  const fetchAnnouncements = useCallback(async () => {
    if (isFetchingRef.current) return;
    if (Date.now() < backoffUntilRef.current) return;
    if (typeof document !== 'undefined' && document.visibilityState === 'hidden') return;
    isFetchingRef.current = true;
    try {
      const response = await apiHelper.get('/announcements/me');
      const data = response.data || [];

      const modalAnn = data.find((a) => {
        if (!a.displayOptions?.showAsModal) return false;
        const key = String(a._id) + '@' + (a.updatedAt || '');
        return !shownModalKeysRef.current.has(key);
      });
      if (modalAnn) {
        const key = String(modalAnn._id) + '@' + (modalAnn.updatedAt || '');
        shownModalKeysRef.current.add(key);
        setModalAnnouncement(modalAnn);
      }
      backoffUntilRef.current = 0;
    } catch (error) {
      const status = error?.response?.status;
      if (status === 429) {
        const retryAfter = Number(error?.response?.headers?.['retry-after'] || 60);
        backoffUntilRef.current = Date.now() + Math.min(retryAfter * 1000, 120000);
      }
      logger.debug('Error fetching announcements', error, 'Announcements');
    } finally {
      isFetchingRef.current = false;
    }
  }, []);

  useEffect(() => {
    fetchAnnouncements();
    const interval = setInterval(() => {
      if (typeof document !== 'undefined' && document.visibilityState === 'hidden') return;
      fetchAnnouncements();
    }, 60000);
    const handleVisible = () => {
      if (document.visibilityState === 'visible') fetchAnnouncements();
    };
    document.addEventListener('visibilitychange', handleVisible);
    window.addEventListener('focus', fetchAnnouncements);
    return () => {
      clearInterval(interval);
      document.removeEventListener('visibilitychange', handleVisible);
      window.removeEventListener('focus', fetchAnnouncements);
    };
  }, [fetchAnnouncements]);

  const handleMarkAsRead = useCallback(async (announcementId) => {
    try {
      await apiHelper.post(`/announcements/${announcementId}/read`);
    } catch (error) {
      logger.debug('Error marking announcement as read', error, 'Announcements');
    }
  }, []);

  // Deep-link support for push notification clicks when app was closed:
  // Service worker opens /dashboard?announcementId=XXX — pick it up and show that announcement directly
  // even without the polling list. Mirrors NotificationPanel's dialog fetch.
  const openAnnouncementFromId = useCallback(async (announcementId, { replaceHistory = true } = {}) => {
    if (!announcementId) return;
    try {
      const res = await apiHelper.get(`/announcements/view/${announcementId}`);
      const ann = res?.data || res;
      if (ann && ann.title) {
        // Mark key as shown so polling doesn't immediately re-show a different modal
        const key = String(ann._id || announcementId) + '@' + (ann.updatedAt || '');
        shownModalKeysRef.current.add(key);
        setModalAnnouncement(ann);
        // Clean the URL so refresh doesn't re-open the same deep link
        if (replaceHistory) {
          try {
            const url = new URL(window.location.href);
            url.searchParams.delete('announcementId');
            window.history.replaceState({}, '', url.pathname + (url.search ? `?${url.searchParams.toString()}` : '') + url.hash);
          } catch (e) {}
        }
        handleMarkAsRead(announcementId);
      }
    } catch (error) {
      // Not targeted / not found (e.g. specific-user announcement for another
      // account opened via a shared ?announcementId= link) — drop the param
      // so it doesn't linger and must never show to the wrong user.
      try {
        if (replaceHistory) {
          const url = new URL(window.location.href);
          if (url.searchParams.has('announcementId')) {
            url.searchParams.delete('announcementId');
            window.history.replaceState({}, '', url.pathname + (url.search ? `?${url.searchParams.toString()}` : '') + url.hash);
          }
        }
      } catch (e) {}
      logger.debug('Error opening announcement from deep link', error, 'Announcements');
    }
  }, [handleMarkAsRead]);

  const checkAnnouncementDeepLink = useCallback(() => {
    try {
      const params = new URLSearchParams(window.location.search);
      const annId = params.get('announcementId');
      if (annId) {
        openAnnouncementFromId(annId);
      }
    } catch (e) {}
  }, [openAnnouncementFromId]);

  useEffect(() => {
    checkAnnouncementDeepLink();
    const handlePop = () => checkAnnouncementDeepLink();
    window.addEventListener('popstate', handlePop);
    return () => window.removeEventListener('popstate', handlePop);
  }, [checkAnnouncementDeepLink]);

  // Also handle when user is already on page and push is clicked (SW navigates existing tab)
  // Some browsers deliver focus+navigation but our check on mount covers it via location change.
  // Additionally support programmatic event from service worker clients if needed.
  useEffect(() => {
    const handleNewAnnouncement = (ev) => {
      const detail = ev && ev.detail ? ev.detail : null;
      if (detail && detail.referenceId) {
        // If toast dispatched new announcement while we have a deep link param, prefer deep link
        const params = new URLSearchParams(window.location.search);
        if (!params.get('announcementId')) {
          fetchAnnouncements();
        }
      } else {
        fetchAnnouncements();
      }
    };
    window.addEventListener('ui:new-announcement', handleNewAnnouncement);
    return () => window.removeEventListener('ui:new-announcement', handleNewAnnouncement);
  }, [fetchAnnouncements]);

  // Open the SAME modal when an announcement is clicked in the notification
  // panel. This guarantees the panel click looks/behaves exactly like a fresh
  // announcement popup (same component, same fetch, same read/dismiss flow).
  useEffect(() => {
    const handleOpenFromPanel = (ev) => {
      const detail = (ev && ev.detail) || {};
      const directAnn = detail.announcement;
      const id = detail.announcementId || detail.referenceId;
      if (directAnn && directAnn.title) {
        const key = String(directAnn._id || id || directAnn.title) + '@' + (directAnn.updatedAt || '');
        shownModalKeysRef.current.add(key);
        setModalAnnouncement(directAnn);
        const markId = directAnn._id || id;
        if (markId) handleMarkAsRead(markId);
        return;
      }
      if (id) {
        // Don't touch the URL for panel clicks — only deep links clean it.
        openAnnouncementFromId(id, { replaceHistory: false });
      }
    };
    window.addEventListener('ui:open-announcement', handleOpenFromPanel);
    return () => window.removeEventListener('ui:open-announcement', handleOpenFromPanel);
  }, [openAnnouncementFromId, handleMarkAsRead]);

  const handleDismiss = useCallback(async (announcementId) => {
    try {
      await apiHelper.post(`/announcements/${announcementId}/dismiss`);
      setModalAnnouncement(null);
    } catch (error) {
      logger.debug('Error dismissing announcement', error, 'Announcements');
    }
  }, []);

  const handleCloseModal = useCallback(() => {
    if (modalAnnouncement) {
      handleMarkAsRead(modalAnnouncement._id);
      if (modalAnnouncement.displayOptions?.dismissible) {
        handleDismiss(modalAnnouncement._id);
      } else {
        setModalAnnouncement(null);
      }
    }
  }, [modalAnnouncement, handleMarkAsRead, handleDismiss]);

  if (!modalAnnouncement) return null;

  const accentColor = getAccentColor(modalAnnouncement);
  const ModalIcon = ICON_MAP[modalAnnouncement.customStyle?.iconType] || FaBullhorn;
  const modalBg = modalAnnouncement.customStyle?.backgroundColor || null;
  const modalText = modalAnnouncement.customStyle?.textColor || null;

  return (
    <div className="announcement-modal-overlay" onClick={handleCloseModal}>
      <div
        className={`announcement-modal theme-${modalAnnouncement.theme || 'default'}`}
        onClick={(e) => e.stopPropagation()}
        style={{
          borderTopColor: accentColor,
          ...(modalBg && { background: modalBg }),
        }}
      >
        <div
          className="announcement-modal__header"
          style={modalBg ? { background: modalBg } : undefined}
        >
          <div
            className="announcement-modal__icon"
            style={{ background: `${accentColor}1a`, color: accentColor }}
          >
            <ModalIcon />
          </div>
          <h2 style={modalText ? { color: modalText } : undefined}>
            {modalAnnouncement.title}
          </h2>
          {modalAnnouncement.displayOptions?.dismissible && (
            <button
              className="announcement-modal__close"
              onClick={handleCloseModal}
              style={modalText ? { color: modalText } : undefined}
            >
              <FaTimes />
            </button>
          )}
        </div>
        <div className="announcement-modal__body">
          {modalAnnouncement.mediaType === 'image' && modalAnnouncement.mediaUrl && (
            <div className="announcement-modal__media">
              <img src={modalAnnouncement.mediaUrl} alt={modalAnnouncement.title} />
            </div>
          )}
          {modalAnnouncement.mediaType === 'video' && modalAnnouncement.mediaUrl && (
            <div className="announcement-modal__media">
              <video src={modalAnnouncement.mediaUrl} controls />
            </div>
          )}
          {modalAnnouncement.content && (
            <p style={{ whiteSpace: 'pre-wrap', ...(modalText && { color: modalText }) }}>
              {modalAnnouncement.content}
            </p>
          )}
        </div>
        <div
          className="announcement-modal__footer"
          style={modalBg ? { background: modalBg } : undefined}
        >
          <button
            className="btn-primary"
            style={{ background: accentColor }}
            onClick={handleCloseModal}
          >
            Got it
          </button>
        </div>
      </div>
    </div>
  );
};

export default AnnouncementModal;
